import { computed, ref, watch, nextTick } from 'vue';
import type { TableCell } from '../model/form.types';
import type { FormBuilderInteractionMode } from '../lib/interaction-mode';

export interface TableCellInteractionContext {
  cell: TableCell;
  isSelected?: boolean;
  isEditable?: boolean;
  allowScopeHover?: boolean;
  interactionMode?: FormBuilderInteractionMode;
}

export interface TableCellInteractionEmit {
  (e: 'select', row: number, col: number): void;
  (e: 'mousedown', row: number, col: number, event: MouseEvent): void;
  (e: 'mouseenter', row: number, col: number): void;
  (e: 'mousemove', row: number, col: number, event: MouseEvent): void;
  (e: 'select-content', row: number, col: number, event: MouseEvent | KeyboardEvent): void;
  (e: 'close-rules-menu'): void;
}

/** Cell DOM interactions (select/hover/keyboard) + focus management + aria label. */
export function useTableCellInteractions(
  props: TableCellInteractionContext,
  emit: TableCellInteractionEmit
) {
  const cellRef = ref<HTMLElement | null>(null);
  const canEmitScopeHover = computed(() => props.isEditable || props.allowScopeHover);

  const isFieldSelectionMode = () =>
    props.interactionMode === 'rules' || props.interactionMode === 'permissions';

  const handleClick = () => {
    if (
      !props.isEditable &&
      props.interactionMode !== 'builder' &&
      !(props.interactionMode === 'rules' && props.cell.content)
    ) {
      return;
    }

    emit('select', props.cell.rowIndex, props.cell.colIndex);
  };

  const handleMouseDown = (event: MouseEvent) => {
    if (!props.isEditable) return;

    // If clicking on cell content, don't select the cell
    const target = event.target as HTMLElement;
    if (target.closest('.cell-content')) {
      return;
    }

    emit('mousedown', props.cell.rowIndex, props.cell.colIndex, event);
  };

  const handleMouseEnter = () => {
    if (!canEmitScopeHover.value) return;
    emit('mouseenter', props.cell.rowIndex, props.cell.colIndex);
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (!canEmitScopeHover.value) return;
    emit('mousemove', props.cell.rowIndex, props.cell.colIndex, event);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const isPermissions = props.interactionMode === 'permissions';
    if (!props.isEditable && !isPermissions) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isPermissions) {
        if (props.cell.content) {
          emit('select-content', props.cell.rowIndex, props.cell.colIndex, event);
        }
        return;
      }
      emit('select', props.cell.rowIndex, props.cell.colIndex);
    }
  };

  const handleContentClick = (event: MouseEvent) => {
    if (!props.cell.content) return;

    event.stopPropagation();

    if (isFieldSelectionMode()) {
      emit('select-content', props.cell.rowIndex, props.cell.colIndex, event);
      return;
    }

    emit('select', props.cell.rowIndex, props.cell.colIndex);
  };

  const handleRulesOpenChange = (open: boolean) => {
    if (!open) emit('close-rules-menu');
  };

  const ariaLabel = computed(() => {
    const position = `Célula linha ${props.cell.rowIndex + 1}, coluna ${props.cell.colIndex + 1}`;
    const hasContent = props.cell.content ? 'com conteúdo' : 'vazia';
    return `${position} (${hasContent})`;
  });

  watch(
    () => props.isSelected,
    (isSelected) => {
      if (isSelected && props.isEditable && cellRef.value) {
        nextTick(() => {
          cellRef.value?.focus();
        });
      }
    }
  );

  watch(
    () => props.cell.content,
    (newContent, oldContent) => {
      if (newContent && !oldContent && props.isSelected && props.isEditable && cellRef.value) {
        nextTick(() => {
          cellRef.value?.focus();
        });
      }
    }
  );

  return {
    cellRef,
    ariaLabel,
    handleClick,
    handleMouseDown,
    handleMouseEnter,
    handleMouseMove,
    handleKeyDown,
    handleContentClick,
    handleRulesOpenChange,
  };
}
